// ---------------------------------------------------------------------------
// Minimal ZIP writer (PKZIP APPNOTE format), for the CSV project export (see
// exportCsvProject.ts) — several independent files bundled into one
// download. No new dependency, same reasoning as exportProject.ts's own
// choice of native gzip over a library: CompressionStream("deflate-raw")
// gives raw DEFLATE (ZIP's "method 8") without a zlib/gzip wrapper, and the
// app already requires the Compression Streams API (same browser support as
// gzip, used there). CRC-32 has no native Web API equivalent, so it's the
// one piece implemented by hand here — the standard table-based algorithm.
//
// No Zip64: this only ever bundles a handful of small CSVs (a project's
// worth of sub-basins/aquifers/zones + one file per model + a report),
// nowhere near the 65535-entry/4GB limits Zip64 exists for.
// ---------------------------------------------------------------------------

const CRC32_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array<ArrayBuffer>): number {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    crc = CRC32_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

async function deflateRaw(bytes: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  const stream = new Blob([bytes]).stream().pipeThrough(new CompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** MS-DOS date/time pair ZIP headers use (2-second resolution, years since 1980). */
function dosDateTime(date: Date): { time: number; date: number } {
  const time =
    (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2);
  const dosDate =
    ((date.getFullYear() - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate();
  return { time, date: dosDate };
}

interface ZipEntryInput {
  /** Forward slashes only — the format the ZIP spec expects, and what every unzip tool assumes. */
  name: string;
  content: Uint8Array<ArrayBuffer>;
}

/** Builds a ZIP file (Blob) from a flat list of entries, compressed with DEFLATE. */
export async function buildZip(entries: ZipEntryInput[]): Promise<Blob> {
  const { time, date } = dosDateTime(new Date());
  const encoder = new TextEncoder();
  const parts: BlobPart[] = [];
  const centralParts: BlobPart[] = [];
  let offset = 0;
  let centralSize = 0;

  for (const entry of entries) {
    const nameBytes = encoder.encode(entry.name);
    const compressed = await deflateRaw(entry.content);
    const crc = crc32(entry.content);

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true); // version needed to extract (2.0)
    local.setUint16(6, 0, true); // general purpose flags
    local.setUint16(8, 8, true); // compression method: deflate
    local.setUint16(10, time, true);
    local.setUint16(12, date, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, compressed.length, true);
    local.setUint32(22, entry.content.length, true);
    local.setUint16(26, nameBytes.length, true);
    local.setUint16(28, 0, true); // extra field length
    parts.push(local.buffer, nameBytes, compressed);

    const central = new DataView(new ArrayBuffer(46));
    central.setUint32(0, 0x02014b50, true);
    central.setUint16(4, 20, true); // version made by
    central.setUint16(6, 20, true); // version needed to extract
    central.setUint16(8, 0, true); // general purpose flags
    central.setUint16(10, 8, true); // compression method
    central.setUint16(12, time, true);
    central.setUint16(14, date, true);
    central.setUint32(16, crc, true);
    central.setUint32(20, compressed.length, true);
    central.setUint32(24, entry.content.length, true);
    central.setUint16(28, nameBytes.length, true);
    central.setUint16(30, 0, true); // extra field length
    central.setUint16(32, 0, true); // file comment length
    central.setUint16(34, 0, true); // disk number start
    central.setUint16(36, 0, true); // internal file attributes
    central.setUint32(38, 0, true); // external file attributes
    central.setUint32(42, offset, true); // offset of local header
    centralParts.push(central.buffer, nameBytes);
    centralSize += central.byteLength + nameBytes.length;

    offset += local.byteLength + nameBytes.length + compressed.length;
  }

  const centralOffset = offset;
  const eocd = new DataView(new ArrayBuffer(22));
  eocd.setUint32(0, 0x06054b50, true);
  eocd.setUint16(4, 0, true); // disk number
  eocd.setUint16(6, 0, true); // disk where central directory starts
  eocd.setUint16(8, entries.length, true); // central dir records on this disk
  eocd.setUint16(10, entries.length, true); // total central dir records
  eocd.setUint32(12, centralSize, true);
  eocd.setUint32(16, centralOffset, true);
  eocd.setUint16(20, 0, true); // comment length

  return new Blob([...parts, ...centralParts, eocd.buffer], { type: "application/zip" });
}
